import React, {useEffect, useState} from "react";
import {faBullhorn} from "@fortawesome/free-solid-svg-icons";
import {useUserDashboard} from "../store/user";
import LearnerShell from "../components/learner/LearnerShell";
import Paginator from "../components/learner/Paginator";
import EmptyState from "../components/learner/EmptyState";
import {imageUrl, shortDate} from "../lib/format";

const LIMIT = 9;

/**
 * /learner/news — announcements from GET /api/learners/dashboard/news.
 */
const LearnerNews = () => {
  const {dashboardNews, dashboardNewsLoading, dashboardNewsError, fetchDashboardNews} = useUserDashboard();
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({total: 0, totalPages: 1});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchDashboardNews({page, limit: LIMIT});
      if (!cancelled && result.success) {
        setMeta({total: result.total || 0, totalPages: result.totalPages || 1});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchDashboardNews, page]);

  const items = Array.isArray(dashboardNews) ? dashboardNews : [];

  return (
    <LearnerShell
      title="News & announcements"
      subtitle={`${meta.total} update${meta.total === 1 ? "" : "s"} from Fillop`}
      icon={faBullhorn}
    >
      {dashboardNewsError ? (
        <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{dashboardNewsError}</p>
      ) : null}

      {dashboardNewsLoading && items.length === 0 ? (
        <div className="space-y-4">
          {[0, 1, 2].map((index) => (
            <div key={index} className="h-32 animate-pulse rounded-2xl border border-slate-200 bg-white" />
          ))}
        </div>
      ) : items.length > 0 ? (
        <>
          <div className="space-y-4">
            {items.map((item) => {
              const cover = imageUrl(item.image || item.coverImage || item.thumbnail);
              return (
                <article
                  key={item.id || item.title}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row"
                >
                  {cover ? (
                    <img
                      src={cover}
                      alt={item.title || "Announcement"}
                      loading="lazy"
                      className="h-40 w-full shrink-0 rounded-xl object-cover sm:h-28 sm:w-44"
                    />
                  ) : null}
                  <div className="min-w-0">
                    <p className="m-0 text-[11px] font-semibold uppercase tracking-wider text-blue-800">
                      {shortDate(item.publishedAt || item.createdAt) || "Announcement"}
                    </p>
                    <h2 className="m-0 mt-1 text-base font-semibold text-slate-900">
                      {item.title || item.heading || "Announcement"}
                    </h2>
                    {item.message || item.body || item.content ? (
                      <p className="m-0 mt-1 whitespace-pre-line text-sm leading-6 text-slate-600">
                        {item.message || item.body || item.content}
                      </p>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
          <Paginator page={page} totalPages={meta.totalPages} onChange={setPage} />
        </>
      ) : (
        <EmptyState icon={faBullhorn} title="No announcements yet" hint="Product news and updates will appear here." />
      )}
    </LearnerShell>
  );
};

export default LearnerNews;
